<template>
  <div class="modal is-active">
    <div class="modal-background" />
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          Editing user: {{ userProfile.firstName }} {{ userProfile.lastName }} (#{{ userProfile.id }})
        </p>
        <button
          id="modal-close-btn"
          type="button"
          class="delete"
          aria-label="close"
          @click.prevent="$emit('close')"
        />
      </header>
      <section class="modal-card-body">
        <div class="columns is-multiline">
          <div class="column is-12">
            <validation-observer
              ref="profile-fields"
              tag="form"
            >
              <profile
                v-bind="{
                  type: 'tempUser',
                  formTitle: 'Edit Profile',
                  formDesc: 'The profile information is used when starting a new filing.',
                  showEmail: true,
                  skipRequirements: true,
                }"
                @profile="userProfile = $event"
              />
            </validation-observer>
            <hr>
            <positions
              v-bind="{ type: 'tempUser' }"
              @positions="positions = $event"
              @hasPositions="hasPositions = $event"
              @hasPrimaryPosition="hasPrimaryPosition = $event"
              @hasPositionsReady="hasPositionsReady = $event"
            />
            <validation-observer
              v-if="componentAccess('statement-requirements', 'view')"
              ref="statement-requirements-fields"
              tag="form"
            >
              <statement-requirements
                :read-only="!componentAccess('statement-requirements', 'edit')"
                v-bind="{ type: 'tempUser', required: false }"
                @requiredStatements="requiredStatements = $event"
              />
            </validation-observer>
            <system-access
              v-if="componentAccess('system-access', 'view')"
              :read-only="!componentAccess('system-access', 'edit')"
              v-bind="{ type: 'tempUser', requester }"
              @systemAccessRoles="sysAccessRoles = $event"
              @systemAccessHasRoles="sysAccessHasRoles = $event"
            />
            <dept-access
              v-if="deptAccessView"
              v-bind="{ type: 'tempUser', requester }"
              :read-only="!deptAccessEdit"
              @deptAccess="deptAccess = $event"
            />
            <user-access-status
              :read-only="!componentAccess('user-access', 'view')"
              v-bind="{ type: 'tempUser' }"
              @userAccessStatuses="userAccessStatuses = $event"
            />
            <reset-password-link-gen
              v-if="componentAccess('reset-password-link-gen', 'view') && userProfile.id"
              :user-id="userProfile.id"
            />
          </div>
          <div class="column is-12 has-text-centered">
            <button
              id="modal-save-btn"
              type="button"
              class="button is-primary"
              :disabled="isSaving"
              @click="saveAndClose"
            >
              Save Profile
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
<script>

import Profile from '@/components/Profile';
import Positions from '@/components/Positions';
import StatementRequirements from '@/components/StatementRequirements';
import SystemAccess from '@/components/SystemAccess';
import UserAccessStatus from '@/components/UserAccessStatus';
import DeptAccess from '@/components/DeptAccess';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import ResetPasswordLinkGen from '@/components/ResetPasswordLinkGen';

export default {
  name: 'User',
  components: {
    Profile,
    Positions,
    StatementRequirements,
    SystemAccess,
    UserAccessStatus,
    DeptAccess,
    ResetPasswordLinkGen,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data() {
    return {
      userProfile: {},
      isSaving: false,
      positions: [],
      hasPositions: false,
      hasPrimaryPosition: true,
      requiredStatements: {},
      sysAccessRoles: {},
      userAccessStatuses: {},
      deptAccess: [],
      sysAccessHasRoles: false,
    };
  },
  computed: {
    deptAccessView () {
      return this.componentAccess('dept-access', 'view') && this.sysAccessRoles.deptHead === 'Y';
    },
    deptAccessEdit () {
      return this.componentAccess('dept-access', 'edit') && this.sysAccessRoles.deptHead === 'Y';
    },
    adminProfile () {
      return this.$store.state.user.profile;
    },
    requester () {
      if (this.adminProfile.sysAdmin === 'Y') {
        return 'admin';
      }
      if (this.adminProfile.deptHead === 'Y') {
        return 'deptHead';
      }
      return null;
    },
  },
  async mounted () {
    this.ready = true;
  },
  methods: {
    componentAccess (component, type) {
      let access = {
        view: [],
        edit: [],
      };
      if (this.adminProfile.sysAdmin === 'Y') {
        access.view.push('statement-requirements');
        access.edit.push('statement-requirements');
        access.view.push('system-access');
        access.edit.push('system-access');
        access.view.push('user-access');
        access.edit.push('user-access');
        access.view.push('dept-access');
        access.edit.push('dept-access');
        access.view.push('reset-password-link-gen');
      }
      if (this.adminProfile.deptHead === 'Y') {
        access.view.push('statement-requirements');
        access.edit.push('statement-requirements');
        access.view.push('system-access');
        // access.edit.push('system-access');
        access.view.push('user-access');
        access.edit.push('user-access');
        access.view.push('dept-access');
        // access.edit.push('dept-access');
        access.view.push('reset-password-link-gen');
      }
      return access[type].includes(component);
    },
    async saveAndClose () {

      let isValidProfile = true;
      let isValidStatmentReqs = true;
      let isValidDeptAccess = true;

      if (this.$refs['profile-fields']) {
        isValidProfile = await this.$refs['profile-fields'].validate();
      }

      if (this.$refs['statement-requirements-fields']) {
        isValidStatmentReqs = await this.$refs['statement-requirements-fields'].validate();
      }

      if (this.deptAccess.length === 0 && this.sysAccessRoles.deptHead === 'Y') {
        isValidDeptAccess = false;
      }

      this.isSaving = true;

      if (
        !isValidProfile ||
        !isValidStatmentReqs ||
        !isValidDeptAccess ||
        !this.sysAccessHasRoles
      ) {
        let warnings = [];
        warnings.push("There are some validation issues.");
        warnings.push("Please review your information and try again.");
        this.$warning(warnings.join(' '));
        this.isSaving = false;
        return false;
      }

      if (this.requiredStatements) {
        this.userProfile = Object.assign(this.userProfile, this.requiredStatements);
      }

      if (this.userAccessStatuses) {
        this.userProfile = Object.assign(this.userProfile, this.userAccessStatuses);
      }

      if (this.adminProfile.sysAdmin === 'Y') {

        if (this.sysAccessRoles) {
          this.userProfile = Object.assign(this.userProfile, this.sysAccessRoles);
        }

      }

      let promises = [];

      promises.push(this.$store.dispatch('user/updateUserProfile', { fields: this.userProfile, userId: this.userProfile.id }));

      if (this.hasPositions) {
        promises.push(this.$store.dispatch('user/saveUserPositions', {
          positions: this.positions,
          userId: this.userProfile.id,
        }));
      }


      if (this.sysAccessRoles.deptHead === 'Y') {
        promises.push(this.$store.dispatch('user/saveUserDepts', {
          agencyIds: this.deptAccess,
          userId: this.userProfile.id,
        }));
      }

      Promise.all(promises).then(responses => {
        this.isSaving = false;
        let notSaved = responses.some(response => response !== true);
        if (notSaved) {
          this.$warning("There was an issue saving this user's profile.");
        } else {
          this.$success("User profile saved. You may need to refresh this screen to see changes in the table.");
          this.$emit('close');
        }
      });
    },
    updatePositions (positions) {
      this.positions = positions;
    },
  },
};
</script>
